// The ONE resolver every patient-chart open goes through (owner rule
// 2026-09-29: "When an appointment for today exists, we should NEVER open a
// past appointment").
//
// Given the card an opener passed (a dashboard card of any date: a card click,
// a search or hand-off result, the dashboard's past-visit fallback, a
// notification), returns the card the chart must open on:
//   - the card itself when it is already the patient's non-cancelled visit
//     today (no network call: today's charts open exactly as before);
//   - otherwise the patient's visit today, when there is one: the dashboard's
//     own card for it (searched for today's date), or, when the dashboard does
//     not return it (one card per patient can be a cancelled row; a location
//     view can hide it), a card merged from GET /appointments/{pid}/future;
//   - otherwise the card as passed (a past/future visit, or no visit), which
//     the chart labels "Past visit · 09-08 · Dr. Adupa".
// The network calls are injected so this runs under node tests.

import {
  classifyVisit,
  needsChartResolution,
  pickTodayRow,
  mergeTodayAppointment,
  rowsOfBody,
  samePatient,
  sameAppointmentId,
  visitDayKey,
} from "./visitGuard.js";

// searchToday(patientid) -> body of POST /appointments/dashboard for today's
//   clinic date with searchflag = patientid.
// fetchFuture(patientid) -> body of GET /appointments/{patientid}/future.
export const resolveChartVisit = async (card, { searchToday, fetchFuture, now = new Date() } = {}) => {
  const from = classifyVisit(card, now);
  if (!needsChartResolution(card, now)) {
    return { card, switched: false, merged: false, verified: true, from, to: from };
  }
  const pid = card?.patientid;
  if (pid === undefined || pid === null || pid === "") {
    return { card, switched: false, merged: false, verified: false, from, to: from };
  }

  let todayRows = [];
  let searchOk = false;
  if (typeof searchToday === "function") {
    try {
      todayRows = rowsOfBody(await searchToday(pid));
      searchOk = true;
    } catch (_) {
      searchOk = false;
    }
  }
  const todayRow = pickTodayRow(todayRows, pid, now);
  if (todayRow) {
    return {
      card: todayRow,
      switched: !sameAppointmentId(todayRow.appointmentid, card?.appointmentid),
      merged: false,
      verified: true,
      from,
      to: classifyVisit(todayRow, now),
    };
  }

  let futureRows = [];
  let futureOk = false;
  if (typeof fetchFuture === "function") {
    try {
      futureRows = rowsOfBody(await fetchFuture(pid));
      futureOk = true;
    } catch (_) {
      futureOk = false;
    }
  }
  const todayAppt = pickTodayRow(futureRows, pid, now);
  if (todayAppt) {
    // Patient fields from the dashboard's own card for today when it returned
    // one (e.g. the cancelled row it picked), else from the card passed in.
    const todayKey = from.todayKey;
    const base =
      todayRows.find((r) => r && samePatient(r.patientid, pid) && visitDayKey(r) === todayKey) ||
      card;
    const merged = mergeTodayAppointment(base, todayAppt);
    return {
      card: merged,
      switched: !sameAppointmentId(merged.appointmentid, card?.appointmentid),
      merged: true,
      verified: true,
      from,
      to: classifyVisit(merged, now),
    };
  }

  return {
    card,
    switched: false,
    merged: false,
    // Both lookups answered and neither has a visit today.
    verified: searchOk && futureOk,
    from,
    to: from,
  };
};
